import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  MenuList,
  MenuItem,
  Popover,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { usePolyAuth } from '../hooks/PolyAuthContext';
import { useTokenExpiry } from '../hooks/useTokenExpiry';
import { decodeVerificationClaims } from '../utils/jwt';
import { formatProofLabel } from '../utils/proofFormatters';
import PolyguardLogo from './PolyguardLogo';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Returns a color token based on remaining time in ms.
 *   > 5 min  → success (green)
 *   > 1 min  → warning (amber)
 *   else     → error   (red)
 */
const countdownColor = (timeRemaining) => {
  if (timeRemaining == null || timeRemaining <= 60_000) return 'error.main';
  if (timeRemaining <= 300_000) return 'warning.main';
  return 'success.main';
};

/** First letter(s) for an Avatar initials fallback. */
const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

const IdentityBar = ({
  onLogin,
  onLogout,
  themeString = 'light',
  extraMenuItems = [],
  logoSrc = '/polyguard-logo.png',
  title,
  titlePosition = 'inline',
  accounts,
}) => {
  const { t } = useTranslation('shared');
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    isAuthenticated,
    accountRoles,
    accountId,
    changeAccount,
    userName,
    knownAccounts,
    login,
    logout,
    verificationJWT,
  } = usePolyAuth();

  // Decode verification claims for photo + proofs
  const verificationClaims = decodeVerificationClaims(verificationJWT);
  const { timeRemaining, isExpired, formattedTime } = useTokenExpiry(verificationJWT, 'verificationJWT');

  const hasKnownAccounts = knownAccounts && Object.keys(knownAccounts).length > 0;
  const hasPolyguardIdentity = isAuthenticated && userName;

  // Derive the current account name from accountRoles
  const currentAccountName = accountId && accountRoles?.[accountId]?.name;
  const displayName = currentAccountName
    ? `${userName} (${currentAccountName})`
    : userName;

  // Other accounts for the switcher (exclude current)
  const otherAccounts = accounts
    ? accounts.filter(a => String(a.id) !== String(accountId)).map(a => [String(a.id), { name: a.name }])
    : Object.entries(accountRoles || {}).filter(
        ([id]) => id !== String(accountId)
      );

  const handleLoginClick = async () => {
    try {
      const { session_jwt } = await login({
        requiredProofs: [
          "name",
          "pg_attestation_key_id",
          "pg_photo_verification_url",
          "pg_high_certainty",
          "pg_presence",
        ],
      });
      onLogin?.(session_jwt);
    } catch (err) {
      console.error('IdentityBar login error:', err);
    }
  };

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    logout();
    onLogout?.();
  };

  const handleAccountSwitch = (targetAccountId) => {
    handleClose();
    changeAccount(targetAccountId);
  };

  // Three-state button text
  let buttonText;
  if (hasKnownAccounts) {
    buttonText = t('identityBar.refreshIdentity');
  } else {
    buttonText = t('identityBar.linkIdentity');
  }

  const popoverOpen = Boolean(anchorEl);

  // Glassmorphic paper styles (shared between old Menu and new Popover)
  const paperSx = {
    mt: 1.5,
    borderRadius: '12px',
    minWidth: 280,
    maxWidth: 340,
    background: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${isDark ? 'rgba(45, 140, 255, 0.2)' : theme.palette.divider}`,
    boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
  };

  const menuItemSx = {
    py: 1.5,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(45, 140, 255, 0.1)',
      transform: 'translateX(5px)',
    },
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 1,
      justifyContent: 'space-between',
      position: 'relative',
      borderRadius: '20px',
      padding: '8px',
    }}>
      {/* Left: logo + title */}
      <Box sx={{
        display: 'flex',
        alignItems: titlePosition === 'below' ? 'flex-start' : 'center',
        flexDirection: titlePosition === 'below' ? 'column' : 'row',
        gap: titlePosition === 'below' ? 0.5 : 1.5,
        minWidth: 0,
        flexShrink: 1,
      }}>
        <PolyguardLogo width={isMobile ? 120 : 160} sx={{ flexShrink: 0 }} />
        {title && (
          <Typography
            variant={titlePosition === 'below' ? 'overline' : 'h6'}
            sx={{
              fontWeight: titlePosition === 'below' ? 600 : 500,
              color: 'text.secondary',
              ...(titlePosition === 'below' && {
                letterSpacing: '0.95em',
                fontSize: '0.85rem',
                fontWeight: 300,
                lineHeight: 1.2,
                ml: '4px',
                transform: 'scaleX(1.1)',
                transformOrigin: 'left',
              }),
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      {/* Right: language switcher + identity chip or login button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageSwitcher size="small" />
        {hasPolyguardIdentity ? (
          <>
            {/* Clickable chip with avatar + countdown */}
            <Box
              onClick={handleOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 0 : 0.75,
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: isMobile ? '50%' : '16px',
                px: isMobile ? 0.5 : 1.5,
                py: 0.5,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(45, 140, 255, 0.3)',
                  '& .identity-countdown': { color: 'white !important' },
                  '& .MuiAvatar-root': { borderColor: 'rgba(255,255,255,0.5)' },
                  '& .identity-arrow': { color: 'white' },
                },
              }}
            >
              <Avatar
                src={verificationClaims?.photoUrl || undefined}
                alt={userName}
                sx={{
                  width: isMobile ? 32 : 28,
                  height: isMobile ? 32 : 28,
                  fontSize: '0.75rem',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {initials(userName)}
              </Avatar>

              {/* Hide name and countdown on mobile - show only in popover */}
              {!isMobile && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {displayName}
                  </Typography>

                  {formattedTime && (
                    <Typography
                      className="identity-countdown"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: countdownColor(timeRemaining),
                        whiteSpace: 'nowrap',
                        ml: 0.25,
                      }}
                    >
                      · {formattedTime}
                    </Typography>
                  )}
                </>
              )}

              {!isMobile && (
                <ArrowDropDownIcon
                  className="identity-arrow"
                  sx={{ fontSize: 18, color: 'text.secondary', ml: -0.25 }}
                />
              )}
            </Box>

            {/* Rich Popover */}
            <Popover
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: paperSx } }}
            >
              {/* Identity card header */}
              <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                <Avatar
                  src={verificationClaims?.photoUrl || undefined}
                  alt={userName}
                  sx={{
                    width: 64,
                    height: 64,
                    fontSize: '1.5rem',
                    border: '2px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  {initials(userName)}
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {userName}
                  </Typography>
                  {currentAccountName && (
                    <Typography variant="body2" color="text.secondary">
                      {currentAccountName}
                    </Typography>
                  )}
                  {formattedTime && (
                    <Typography
                      variant="body2"
                      sx={{ color: countdownColor(timeRemaining), fontWeight: 500, mt: 0.5 }}
                    >
                      {isExpired ? t('identityBar.identityExpired', { time: formattedTime }) : t('identityBar.identityRemaining', { time: formattedTime })}
                    </Typography>
                  )}
                  {(isExpired || !verificationJWT) && (
                    <Button
                      size="small"
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                      onClick={() => { handleClose(); handleLoginClick(); }}
                      sx={{ mt: 0.5, textTransform: 'none', fontWeight: 500, p: 0, minWidth: 0, justifyContent: 'flex-start' }}
                    >
                      {t('identityBar.refreshIdentity')}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Proof chips */}
              {verificationClaims?.proofs?.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                    {t('identityBar.verifiedProofs')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {verificationClaims.proofs.map((proof) => (
                      <Chip
                        key={proof}
                        label={formatProofLabel(proof)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Menu items */}
              {(otherAccounts.length > 0 || extraMenuItems.length > 0) && (
                <Divider sx={{ borderColor: 'divider' }} />
              )}

              <MenuList dense sx={{ py: 0.5 }}>
                {otherAccounts.map(([id, role]) => (
                  <MenuItem
                    key={id}
                    onClick={() => handleAccountSwitch(id)}
                    sx={menuItemSx}
                  >
                    <Typography variant="body2">
                      {t('identityBar.switchToAccount', { accountName: role?.name || `Account ${id}` })}
                    </Typography>
                  </MenuItem>
                ))}

                {extraMenuItems.map((item, index) => (
                  <MenuItem
                    key={`extra-${index}`}
                    onClick={() => { handleClose(); item.onClick?.(); }}
                    sx={{ ...menuItemSx, ...item.sx }}
                  >
                    {item.icon && (
                      <Box component="span" sx={{ mr: 1, display: 'inline-flex', verticalAlign: 'middle' }}>
                        {item.icon}
                      </Box>
                    )}
                    {item.label}
                  </MenuItem>
                ))}
              </MenuList>

              <Divider sx={{ borderColor: 'divider' }} />

              <MenuList dense sx={{ py: 0.5 }}>
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    ...menuItemSx,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      transform: 'translateX(5px)',
                    },
                  }}
                >
                  {t('identityBar.logout')}
                </MenuItem>
              </MenuList>
            </Popover>
          </>
        ) : (
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleLoginClick}
          >
            {buttonText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default IdentityBar;
